import { AddTaskNoteRequest, CreateTaskEventResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useTasksTranslation } from '../hooks';
import {
    addOrUpdateAllTasksCache,
    addOrUpdateTaskEventsCache
} from '../queries/tasks-cache';

type UseAddTaskNoteActionProps = {
    taskId: string;
};

type UseAddTaskNoteActionData = {
    addNote: (note: string) => Promise<CreateTaskEventResponse>;
    loading: boolean;
    error: string;
};

/**
 * Adding a note to a task's event timeline.
 *
 * No `isAllowed`/`reason`: whether a viewer may add a note is decided a screen
 * up by `useTaskPermissions(task)` — it needs the whole task to answer, not
 * just its id — and reaches the button as `canAddNote`.
 */
export function useAddTaskNoteAction(
    props: UseAddTaskNoteActionProps
): UseAddTaskNoteActionData {
    const { taskId } = props;

    const { t } = useTasksTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. `POST /v3/tasks/{taskId}/notes`
    // declares `BAD_REQUEST`, `TASK_NOT_FOUND` and `USER_NOT_FOUND`; anything
    // else is a transport or auth failure, which `getFallbackErrorKey` names.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return (
                    error.response?.data.message.join(
                        t('shared.tasks.list_separator')
                    ) ?? ''
                );
            }
            if (isApiError(error, 'TASK_NOT_FOUND')) {
                return t('shared.errors.task_not_found');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: AddTaskNoteRequest) =>
            openApiRef.current.tasks
                .addNote(taskId, variables)
                .then((response) => response.data),
        onSuccess: (response) => {
            // Write the server's answer into the cache — never invalidate and
            // refetch what the response already handed you.
            const { task, events } = response.data;
            addOrUpdateAllTasksCache(queryClient, task);
            addOrUpdateTaskEventsCache(queryClient, taskId, events);

            UIToast.success(t('actions.add_note.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        addNote: (note) => mutation.mutateAsync({ note }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
